/**
 * Cache Management Utility
 * Provides functions to manage browser cache, service worker cache, and localStorage
 */

export const cacheManager = {
  /**
   * Clear all service worker caches
   */
  async clearServiceWorkerCache(): Promise<boolean> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Service worker caches cleared');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error clearing service worker cache:', error);
      return false;
    }
  },

  /**
   * Unregister all service workers
   */
  async unregisterServiceWorkers(): Promise<boolean> {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
        console.log('Service workers unregistered');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error unregistering service workers:', error);
      return false;
    }
  },

  /**
   * Clear specific localStorage items (preserve important data)
   */
  clearNonEssentialLocalStorage(): void {
    const keysToPreserve = [
      'cart',
      'wishlist',
      'customerDetails',
      'notificationsEnabled',
      'fcmToken'
    ];

    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (!keysToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    });
    console.log('Non-essential localStorage cleared');
  },

  /**
   * Clear all caches and reload the page
   */
  async clearAllCachesAndReload(): Promise<void> {
    try {
      // Clear service worker caches
      await this.clearServiceWorkerCache();
      
      // Clear non-essential localStorage
      this.clearNonEssentialLocalStorage();
      
      // Unregister service workers
      await this.unregisterServiceWorkers();
      
      // Force reload from server (bypass cache)
      window.location.reload();
    } catch (error) {
      console.error('Error clearing caches:', error);
      // Still try to reload even if clearing fails
      window.location.reload();
    }
  },

  /**
   * Soft refresh - just reload without clearing everything
   */
  softRefresh(): void {
    window.location.reload();
  },

  /**
   * Check if service worker is active
   */
  async isServiceWorkerActive(): Promise<boolean> {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        return !!registration?.active;
      }
      return false;
    } catch (error) {
      console.error('Error checking service worker:', error);
      return false;
    }
  },

  /**
   * Get cache size estimate
   */
  async getCacheSize(): Promise<{ usage: number; quota: number } | null> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting cache size:', error);
      return null;
    }
  }
};

/**
 * Format bytes to human readable format
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
